import { Operation } from '../hooks/useOperations/types';
import { ALL_OPERATIONS } from './operations';

type ToggleOperationSelectionMode = {
  hasMultiOperationEnabled: boolean;
  operationsSelected: Operation[];
  operations: Operation[];
  setSelectedOperations: (opertaionsSelected: Operation[]) => void;
  hideAllOperations?: boolean;
};

function getIndividualOperations(operations: Operation[]): Operation[] {
  return operations.filter(({ operationId }) => operationId !== ALL_OPERATIONS);
}

function isAllOperationsObjectSelected(operationsSelected: Operation[]): boolean {
  return operationsSelected.length > 0 && operationsSelected[0].operationId === ALL_OPERATIONS;
}

function areAllIndividualOperationsSelected(
  operationsSelected: Operation[],
  individualOperations: Operation[]
): boolean {
  return (
    individualOperations.length > 0 &&
    operationsSelected.length === individualOperations.length &&
    !isAllOperationsObjectSelected(operationsSelected)
  );
}

function isInAllOperationsMode(
  operationsSelected: Operation[],
  individualOperations: Operation[]
): boolean {
  return (
    isAllOperationsObjectSelected(operationsSelected) ||
    areAllIndividualOperationsSelected(operationsSelected, individualOperations)
  );
}

function areSelectionsEqual(selection1: Operation[], selection2: Operation[]): boolean {
  if (selection1.length !== selection2.length) {
    return false;
  }
  
  const ids1 = selection1.map(op => op.operationId).sort();
  const ids2 = selection2.map(op => op.operationId).sort();
  
  return ids1.every((id, index) => id === ids2[index]);
}

function handleMultiToSingleTransition(
  operationsSelected: Operation[],
  individualOperations: Operation[],
  hideAllOperations: boolean | undefined,
  inAllOperationsMode: boolean
): Operation[] | null {
  // All operations selected, hide all object -> select first
  if (hideAllOperations && inAllOperationsMode) {
    return [individualOperations[0]];
  }
  
  // All operations selected, show all object -> send individual operations
  if (inAllOperationsMode) {
    return individualOperations;
  }
  
  // Multiple operations selected (partial) -> select first only
  if (operationsSelected.length > 1) {
    return [operationsSelected[0]];
  }
  
  return null;
}

function handleSingleToMultiTransition(
  operationsSelected: Operation[],
  individualOperations: Operation[]
): Operation[] | null {
  // All operations object selected -> expand to individual operations
  if (isAllOperationsObjectSelected(operationsSelected)) {
    return individualOperations;
  }
  
  return null;
}

export function toggleOperationSelectionMode({
  hasMultiOperationEnabled,
  operationsSelected,
  operations,
  setSelectedOperations,
  hideAllOperations,
}: ToggleOperationSelectionMode): void {
  const individualOperations = getIndividualOperations(operations);
  const inAllOperationsMode = isInAllOperationsMode(operationsSelected, individualOperations);

  const newSelection = hasMultiOperationEnabled
    ? handleSingleToMultiTransition(operationsSelected, individualOperations)
    : handleMultiToSingleTransition(operationsSelected, individualOperations, hideAllOperations, inAllOperationsMode);

  if (newSelection && !areSelectionsEqual(newSelection, operationsSelected)) {
    setSelectedOperations(newSelection);
  }
}